/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useMemo } from "react";
import { Badge, Button, Combobox, Input } from "@repo/ui";
import { Link } from "react-router-dom";
import { CirclePlus, CircleX } from "lucide-react";
import {
  useLookupCashCenters,
  useLookupCustomers,
} from "services/lookups-mutations";
import { Paths } from "routes/paths";
import { useUser } from "hooks/use-user";
import { formatExcelTimeToHHMMSS, HasPermission } from "utils/helper";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { useToast } from "@chakra-ui/react";
import {
  useExportLocationSlaConf,
  useImportLocationSlaConf,
} from "../../services/locations-apis";
import * as XLSX from "xlsx";
import { useForm } from "react-hook-form";
import ReactHookFormDropdown from "components/forms/react-hook-dropdown";

export default function LocationsFilterBarV2({ getList }) {
  const [filters, setFilters] = useState({});
  const [selectedCashCenter, setSelectedCashCenter] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");

  const [filter, setFilter] = useState(null);
  const [firstLoad, setFirstLoad] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);

  const [exportCashCenters, setExportCashCenters] = useState([]);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const fileInputRef = useRef(null);

  const { mutate: importLocationConf, isPending: isImporting } =
    useImportLocationSlaConf();

  const filterTimeout = useRef(null);
  const user = useUser();
  const toast = useToast();

  const {
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      exportCustomer: null,
    },
  });
  const exportCustomer = watch("exportCustomer");
  const { mutate: exportLocationConf, isPending } = useExportLocationSlaConf();
  const { mutate: getCashCenters, data: cashCentersList } =
    useLookupCashCenters();
  const { mutate: getCustomers, data: customersList } = useLookupCustomers();

  useEffect(() => {
    getCashCenters();
    getCustomers();
  }, []);

  useEffect(() => {
    if (filter !== null) {
      filterTimeout.current = setTimeout(() => {
        getList(filter || "", selectedCashCenter || "", selectedCustomer || "");
        handleAddFilter("filter", filter, "Search");
        clearTimeout(filterTimeout.current);
        filterTimeout.current = null;
      }, 1000);
    }
    return () => {
      if (filterTimeout.current) clearTimeout(filterTimeout.current);
    };
  }, [filter]);
  useEffect(() => {
    if (!firstLoad) {
      getList(filter || "", selectedCashCenter || "", selectedCustomer || "");
    } else {
      setFirstLoad(false);
    }
  }, [selectedCashCenter, selectedCustomer]);

  const normalizedCustomers = useMemo(
    () => [
      { customerId: "ALL", name: "All" },
      ...(customersList?.data || []).map((c) => ({
        value: String(c.customerId),
        label: c.name,
      })),
    ],
    [customersList],
  );

  const normalizedCashCenters = useMemo(
    () => [
      { id: "ALL", name: "All" },
      ...(cashCentersList?.data || []).map((c) => ({
        id: String(c.cashCenterId),
        name: c.name,
      })),
    ],
    [cashCentersList],
  );

  /** EXPORT HANDLERS */

  const handleExportCashCenterChange = (e) => {
    const value = e.value;
    const hasAll = value.some((v) => v.id === "ALL");

    setExportCashCenters(
      hasAll ? [normalizedCashCenters[0]] : value.filter((v) => v.id !== "ALL"),
    );
  };

  // Sheet Builders
  const buildLocationsSheet = (data) =>
    data.map((item) => ({
      CashCenterName: item.cashCenterName,
      CustomerName: item.customerName,
      Barcodes: item.barcode || "",
      LocationId: item.locationId,
      LocationName: item.locationName,
      LocationType: item.locationType,
      LocationCode: item.locationCode,
      Status: item.status,
      Country: item.country,
      State: item.state,
      Emirate: item.emirate,
      City: item.city,
      Address: item.address,
      "First Service Date": item.firstServiceDate || "",
      "Decommissioned date": item.decomissionedDate || "",
      "Maximum Cassettes": item.maximumCassettes || "",
      "Machine Serial No": item.machineSerialNo || "",
      "Site Type": item.siteType || "",
      "Follows Customer SLA": item.followsCustomerSla,
      "Follows CustomerService Window": item.followsCustomerServiceWindow,
    }));

  const buildSlaSheet = (data) => {
    const rows = [];
    data.forEach((item) => {
      (item.siteSLAs || []).forEach((sla) => {
        rows.push({
          CashCenterName: item.cashCenterName,
          CustomerName: item.customerName,
          Barcodes: item.barcode || "",
          LocationId: item.locationId,
          // "Follows customer SLA": item.followsCustomerSla ?? "",
          // ServiceType: sla.ServiceType,
          ActivityType: sla.ActivityType,
          ResponseTime: sla.ResponseTime ?? "",
          ResolutionTime: sla.ResolutionTime ?? "",
        });
      });
    });
    if (rows.length === 0) {
      rows.push({
        CashCenterName: "",
        CustomerName: "",
        Barcodes: "",
        LocationId: "",
        // "Follows customer SLA": "",
        // ServiceType:"",
        ActivityType: "",
        ResponseTime: "",
        ResolutionTime: "",
      });
    }
    return rows;
  };

  const buildAccessTimingsSheet = (data) => {
    const rows = [];
    data.forEach((item) => {
      (item.serviceWindows || []).forEach((window) => {
        rows.push({
          CashCenterName: item.cashCenterName,
          CustomerName: item.customerName,
          Barcodes: item.barcode || "",
          LocationId: item.locationId,
          // "Follows customer service window":
          //   item.followsCustomerServiceWindow ?? "",
          DayOfWeek: window.DayOfWeek,
          StartTime: window.StartTime,
          EndTime: window.EndTime,
          SecondStartTime: window.SecondStartTime ?? "",
          SecondEndTime: window.SecondEndTime ?? "",
        });
      });
    });
    if (rows.length === 0) {
      rows.push({
        CashCenterName: "",
        CustomerName: "",
        Barcodes: "",
        LocationId: "",
        // "Follows customer service window":
        //   item.followsCustomerServiceWindow ?? "",
        DayOfWeek: 0,
        StartTime: "",
        EndTime: "",
        SecondStartTime: "",
        SecondEndTime: "",
      });
    }
    return rows;
  };

  const handleExport = () => {
    const payload = {
      customerIds:
        !exportCustomer || exportCustomer?.customerId === "ALL"
          ? []
          : [exportCustomer], // SINGLE
      cashcenterIds:
        exportCashCenters.length === 1 && exportCashCenters[0].id === "ALL"
          ? []
          : exportCashCenters.map((c) => c.id),
    };

    exportLocationConf(payload, {
      onSuccess: (res) => {
        const apiData = res?.data || [];
        if (!apiData.length) {
          toast({ title: "No data found", status: "info" });
          return;
        }

        const workbook = XLSX.utils.book_new();
        const onwardLocations = [];
        apiData.forEach((location) => {
          location.onwardLocations.forEach((onwardLocation) => {
            if (onwardLocation?.AllowedContainerTypes?.length) {
              onwardLocation?.AllowedContainerTypes?.forEach((container) => {
                onwardLocations.push({
                  CashCenterName: location.cashCenterName,
                  CustomerName: location.customerName,
                  Barcodes: location.barcode || "",
                  LocationId: location.locationId,
                  OnwardLocationBarcode: onwardLocation.Barcode,
                  ContainerCode: container.Code,
                });
              });
            } else {
              onwardLocations.push({
                CashCenterName: location.cashCenterName,
                CustomerName: location.customerName,
                Barcodes: location.barcode || "",
                LocationId: location.locationId,
                OnwardLocationBarcode: onwardLocation.Barcode,
                ContainerCode: "",
              });
            }
          });
        });

        if (onwardLocations.length === 0) {
          onwardLocations.push({
            CashCenterName: "",
            CustomerName: "",
            Barcodes: "",
            LocationId: "",
            OnwardLocationBarcode: "",
            ContainerCode: "",
          });
        }
        const siteRepresentatives = [];
        apiData.forEach((location) => {
          location.siteRepresentatives.forEach((elm) => {
            siteRepresentatives.push({
              CashCenterName: location.cashCenterName,
              CustomerName: location.customerName,
              Barcodes: location.barcode || "",
              LocationId: location.locationId,
              Name: elm.Name,
              EmployeeId: elm.EmployeeId,
              MobileNo: elm.MobileNo,
              Email: elm.Email,
            });
          });
        });

        if (siteRepresentatives.length === 0) {
          siteRepresentatives.push({
            CashCenterName: "",
            CustomerName: "",
            Barcodes: "",
            LocationId: "",
            Name: "",
            EmployeeId: "",
            MobileNo: "",
            Email: "",
          });
        }
        const serviceSchedules = [];
        apiData.forEach((location) => {
          location.serviceSchedules.forEach((elm) => {
            serviceSchedules.push({
              CashCenterName: location.cashCenterName,
              CustomerName: location.customerName,
              Barcodes: location.barcode || "",
              LocationId: location.locationId,
              ScheduleType: elm.ScheduleType,
              ServiceType: elm.ServiceType,
              JobType: elm.JobType,
              RouteNo: elm.RouteNo,
              WeekDays: elm.WeekDays?.join(",") || "",
              MonthDates: elm.MonthDates?.join(",") || "",
            });
          });
        });

        if (serviceSchedules.length === 0) {
          serviceSchedules.push({
            CashCenterName: "",
            CustomerName: "",
            Barcodes: "",
            LocationId: "",
            ScheduleType: "",
            ServiceType: "",
            JobType: "",
            RouteNo: "",
            WeekDays: "",
            MonthDates: "",
          });
        }

        // Locations Sheet
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(buildLocationsSheet(apiData)),
          "Locations",
        );
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(onwardLocations),
          "Onward Locations",
        );
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(siteRepresentatives),
          "Site Representatives",
        );
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(serviceSchedules),
          "Service Schedules",
        );

        // SLA Sheet
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(buildSlaSheet(apiData)),
          "Site SLA",
        );

        // Access Timings Sheet
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(buildAccessTimingsSheet(apiData)),
          "Access Timings",
        );

        XLSX.writeFile(workbook, "Locations_Detail.xlsx");
        toast({ title: "Export successful", status: "success" });
      },
      onError: (err) => {
        toast({
          title: "Export failed",
          description: err.message || "Something went wrong",
          status: "error",
        });
      },
    });
  };

  const handleImportFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
  };

  const handleUploadImport = () => {
    if (!selectedFile) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: "binary" });

      const locationsSheet = XLSX.utils.sheet_to_json(
        workbook.Sheets["Locations"] || [],
      );

      const slaSheet = XLSX.utils.sheet_to_json(
        workbook.Sheets["Site SLA"] || [],
      );

      const siteRepresentativesSheet = XLSX.utils.sheet_to_json(
        workbook.Sheets["Site Representatives"] || [],
      );

      const onwardLocationsSheet = XLSX.utils.sheet_to_json(
        workbook.Sheets["Onward Locations"] || [],
      );

      const serviceSchedulesSheet = XLSX.utils.sheet_to_json(
        workbook.Sheets["Service Schedules"] || [],
      );

      const accessSheet = XLSX.utils.sheet_to_json(
        workbook.Sheets["Access Timings"] || [],
      );

      const locationMap = {};

      /** ---------------------
       * LOCATIONS SHEET
       * --------------------- */
      locationsSheet.forEach((row) => {
        const locationId = String(row.LocationId);

        locationMap[locationId] = {
          locationId,
          followsCustomerSla:
            row["Follows Customer SLA"]?.toString().toLowerCase() || "no",

          followsCustomerServiceWindow:
            row["Follows CustomerService Window"]?.toString().toLowerCase() ||
            "no",

          sla: [],
          onwardLocations: [],
          serviceSchedules: [],
          siteRepresentatives: [],
          serviceWindows: [],
        };
      });
      /** ---------------------
       * Onward Locations SHEET
       * --------------------- */
      onwardLocationsSheet.forEach((row) => {
        const location = locationMap[String(row.LocationId)];
        if (!location) return;
        location.onwardLocations.push({
          locationBarcode: String(row.Barcodes),
          onwardLocationBarcode: String(row.OnwardLocationBarcode),
          containerCode: row.ContainerCode,
        });
      });
      /** ---------------------
       * SLA SHEET
       * --------------------- */
      slaSheet.forEach((row) => {
        const location = locationMap[String(row.LocationId)];
        if (!location) return;

        location.sla.push({
          activityType: row.ActivityType || "",
          responseTime: row.ResponseTime || 0,
          resolutionTime: row.ResolutionTime || 0,
        });
      });

      /** ---------------------
       * Site Representative SHEET
       * --------------------- */
      siteRepresentativesSheet.forEach((row) => {
        const location = locationMap[String(row.LocationId)];
        if (!location) return;

        location.siteRepresentatives.push({
          name: `${row.Name}`,
          employeeId: `${row.EmployeeId}`,
          mobileNo: `${row.MobileNo}`,
          email: `${row.Email}`,
        });
      });
      /** ---------------------
       * service Schedules SHEET
       * --------------------- */
      serviceSchedulesSheet.forEach((row) => {
        const location = locationMap[String(row.LocationId)];
        if (!location) return;
        location.serviceSchedules.push({
          scheduleType: row.ScheduleType,
          serviceType: row.ServiceType,
          jobType: row.JobType,
          routeNo: row.RouteNo || "",
          weekDays: row.WeekDays?.split(",") || [],
          monthDates: String(row.MonthDates || "")?.split(",") || [],
        });
      });
      /** ---------------------
       * ACCESS TIMINGS SHEET
       * --------------------- */
      accessSheet.forEach((row) => {
        const location = locationMap[String(row.LocationId)];
        if (!location) return;

        location.serviceWindows.push({
          dayOfWeek: row.DayOfWeek || 0,
          startTime: row.StartTime
            ? String(formatExcelTimeToHHMMSS(row.StartTime))
            : null,

          endTime: row.EndTime
            ? String(formatExcelTimeToHHMMSS(row.EndTime))
            : null,

          secondStartTime: row.SecondStartTime
            ? String(formatExcelTimeToHHMMSS(row.SecondStartTime))
            : null,

          secondEndTime: row.SecondEndTime
            ? String(formatExcelTimeToHHMMSS(row.SecondEndTime))
            : null,
        });
      });

      const payload = {
        servicePoints: Object.values(locationMap),
      };

      importLocationConf(payload, {
        onSuccess: () => {
          toast({ title: "Import successful", status: "success" });

          setSelectedFile(null);
          setIsImportOpen(false);

          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        },
        onError: (err) => {
          toast({
            title: "Import failed",
            description: err.message || "Something went wrong",
            status: "error",
          });
        },
      });
    };

    reader.readAsBinaryString(selectedFile);
  };

  const handleClearFilter = (key) => {
    const updated = { ...filters };
    delete updated[key];

    if (key === "cashCenter") setSelectedCashCenter("");
    if (key === "customer") setSelectedCustomer("");
    if (key === "filter") setFilter("");

    setFilters(updated);
  };
  const handleAddFilter = (key, value, name) => {
    if (!value) return handleClearFilter(key);
    setFilters((prev) => ({ ...prev, [key]: `${name}: ${value}` }));
  };

  const clearAllFilters = () => {
    setFilters({});
    setSelectedCashCenter("");
    setSelectedCustomer("");
    setFilter("");
  };

  const canAdd = useMemo(
    () => HasPermission(user, "ServicePoint", "Add"),
    [user],
  );

  return (
    <>
      {/* TOP BAR */}
      <div className="flex mb-3 gap-1">
        <div className="min-w-[15%]">
          <Combobox
            placeholder="Select Cash Center"
            data={[
              { label: "Select Cash Center", value: "" },
              ...((cashCentersList?.data || []).map((elm) => ({
                label: elm.name,
                value: elm.cashCenterId,
              })) || []),
            ]}
            value={selectedCashCenter}
            setValue={(value) => {
              setSelectedCashCenter(value);
              const name =
                cashCentersList?.data.find((c) => c.cashCenterId === value)
                  ?.name || "";
              handleAddFilter("cashCenter", name, "Cash Center");
            }}
          />
        </div>

        <div className="min-w-[15%]">
          <Combobox
            placeholder="Select Customer"
            data={[
              { label: "Select Customer", value: "" },
              ...((customersList?.data || []).map((elm) => ({
                label: elm.name,
                value: elm.customerId,
              })) || []),
            ]}
            value={selectedCustomer}
            setValue={(value) => {
              setSelectedCustomer(value);
              const name =
                customersList?.data.find((c) => c.customerId === value)?.name ||
                "";
              handleAddFilter("customer", name, "Customer");
            }}
          />
        </div>
        <div className="w-full">
          <Input
            value={filter}
            placeholder="Search"
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        {canAdd ? (
          <div className="flex gap-2">
            <Link to={Paths.addLocations}>
              <Button variant="secondary" className="flex gap-2">
                <CirclePlus /> Create Location
              </Button>
            </Link>

            <div className="relative">
              <Button
                variant="secondary"
                onClick={() => setIsDropdownOpen((prev) => !prev)}
              >
                Bulk Operations
              </Button>

              {isDropdownOpen ? (
                <div className="absolute right-0 mt-2 w-56 bg-white border rounded shadow-md z-50">
                  <div
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      reset({ Customer: null, exportCustomer: null });
                      setExportCashCenters([]);
                      setIsExportOpen(true);
                      setIsDropdownOpen(false);
                    }}
                  >
                    Download Excel
                  </div>

                  <div
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      setIsImportOpen(true);
                      setIsDropdownOpen(false);
                    }}
                  >
                    Upload Excel
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {/* FILTER CHIPS */}
      <div className="flex mb-3 gap-2 flex-wrap items-end">
        {Object.entries(filters).map(([key, value]) => (
          <Badge key={key} variant="secondary" className="gap-3 pr-1">
            {value}
            <CircleX onClick={() => handleClearFilter(key)} />
          </Badge>
        ))}

        {Object.keys(filters).length > 0 && (
          <Badge variant="secondary" className="gap-3 pr-1">
            Clear All
            <CircleX onClick={clearAllFilters} />
          </Badge>
        )}
      </div>

      {/* EXPORT MODAL */}
      {isExportOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg p-5 w-[380px] space-y-4">
            <h3 className="text-lg font-semibold">Export Locations Detail</h3>

            {/* SINGLE CUSTOMER */}
            <ReactHookFormDropdown
              label="Customer"
              name="exportCustomer"
              placeholder="Select Customer"
              control={control}
              errors={errors}
              data={normalizedCustomers}
              id="customerId"
              text="name"
            />

            {/* MULTI CASH CENTERS */}
            <MultiSelect
              data={normalizedCashCenters}
              textField="name"
              dataItemKey="id"
              value={exportCashCenters}
              onChange={handleExportCashCenterChange}
              placeholder="Select Cash Centers"
            />

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setIsExportOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="secondary"
                disabled={isPending}
                onClick={() => {
                  handleExport();
                  setIsExportOpen(false);
                }}
              >
                Submit
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      {isImportOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg p-5 w-[400px] space-y-4">
            <h3 className="text-lg font-semibold">Import Locations Detail</h3>

            <input
              type="file"
              accept=".xlsx, .xls"
              ref={fileInputRef}
              onChange={handleImportFile}
            />

            {selectedFile ? (
              <p className="text-sm text-gray-600">
                Selected: {selectedFile.name}
              </p>
            ) : null}

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsImportOpen(false);
                  setSelectedFile(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
              >
                Cancel
              </Button>

              {selectedFile ? (
                <Button
                  variant="secondary"
                  disabled={isImporting}
                  onClick={handleUploadImport}
                >
                  {isImporting ? "Uploading..." : "Upload"}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
